import React, { useMemo } from 'react';
import classNames from 'classnames';
import {
  Tooltip,
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Text,
} from '@fluentui/react-components';
import { ChevronDown20Regular } from '@fluentui/react-icons';
import { TooltipHost } from '@fluentui/react';
import { Glyphicon } from '@bingads-webui-react/primitive-unthemed';
import { ResponsiveSize } from '@bingads-webui-campaign-react/pmax-overview-responsive-utils';

import { useStyles } from '../styles/metric-picker-item';

export interface IMetricPickerItemProps {
  isSelected: boolean;
  index: number;
  displayName: string;
  total: string;
  i18n: any;
  hideCurrency: boolean;
  dropdownMetrics: Array<{
    key: string,
    displayName: string,
  }>;
  isComparisonEnabled: boolean;
  responsiveSize: ResponsiveSize;
  onMetricToggle: () => void;
  onMetricDropdownSelect: (key: string) => void;
}

export function MetricPickerItem(props: IMetricPickerItemProps) {
  const {
    isSelected,
    index,
    displayName,
    total,
    i18n,
    hideCurrency,
    dropdownMetrics,
    isComparisonEnabled = false,
    responsiveSize,
    onMetricToggle,
    onMetricDropdownSelect,
  } = props;
  const classes = useStyles();
  const dropdownItems = useMemo(() => dropdownMetrics.map((metric) => {
    const handleDropdownSelect = () => onMetricDropdownSelect(metric.key);
    return (
      <MenuItem
        key={metric.key}
        onClick={handleDropdownSelect}
        className="ba-pc-metric-picker-menu-item"
      >
        {metric.displayName}
      </MenuItem>
    );
  }), [dropdownMetrics, onMetricDropdownSelect]);
  return (
    <div className={classNames('ba-pc-metric-picker-item', classes.metricPickerItem, `ba-pc-metric-picker-item-size-${responsiveSize}`, { [`ba-pc-metric-picker-item-index-${index}`]: isSelected })}>
      <div className={classNames('ba-pc-metric-picker-item__dropdown', classes.dropdown)}>
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Tooltip content={displayName} relationship="label">
              <Button className={classNames('ba-pc-dropdown-trigger_button', classes.dropdownTriggerButton)} appearance="subtle" iconPosition="after" icon={<ChevronDown20Regular />}>
                <Text className={classNames('ba-pc-metric-picker-name', classes.pickerName)} weight="bold">
                  {displayName}
                </Text>
              </Button>
            </Tooltip>
          </MenuTrigger>
          <MenuPopover>
            <MenuList className="ba-pc-metric-picker-menu-list">
              {dropdownItems}
            </MenuList>
          </MenuPopover>
        </Menu>
      </div>
      <Button
        appearance="subtle"
        className={classNames('ba-pc-metric-picker-item-toggle-button', classes.toggleButton)}
        onClick={onMetricToggle}
      >
        {(['Spend', 'Revenue', 'Avg. CPC', 'CPA'].includes(displayName) && hideCurrency) && (
        <TooltipHost
          content={i18n.getString(_TL_('Value is not shown because you have accounts with different currencies.'))}
        >
          <Glyphicon glyph="ba-Info" className="metric-info" />
        </TooltipHost>
        )}

        <div className={classNames('ba-pc-metric-picker-item-image', classes.imageIndicator)}>
          {isSelected &&
            <>
              <div className={classNames('original-marker', classes.marker)} />
              {isComparisonEnabled && <div className={classNames('comparison-marker', classes.marker)} />}
            </>
          }
        </div>
        <Tooltip content={total} relationship="label">
          <Text className={classNames('ba-pc-metric-picker-value', classes.pickerValue)}>
            {total}
          </Text>
        </Tooltip>
      </Button>
    </div>
  );
}
